class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.searchTerm = "";
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchIcon = this.querySelector(".search-icon");
    this.clearIcon = this.querySelector(".clear-icon");
    this.overlay = document.querySelector(".predictive-search-overlay");
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    const debouncedOnChange = debounce(this.onChange.bind(this), 300);
    this.input.addEventListener("input", debouncedOnChange);
    if (this.overlay) {
      this.overlay.addEventListener("click", () => {
        this.close(true);
      });
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    if (this.searchIcon) {
      this.searchIcon.addEventListener("click", (event) => {
        event.preventDefault();
        // behave like focusing the input: focus will trigger onFocus and open the modal/results
        this.input.focus();
      });
    }

    if (this.clearIcon) {
      this.clearIcon.addEventListener("click", (event) => {
        event.preventDefault();
        // clear input and close predictive search
        this.close(true);

        // toggle icons back to default state
        if (this.searchIcon) {
          this.searchIcon.style.display = "block";
        }
        this.clearIcon.style.display = "none";
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newTerm = this.getQuery();

    if (!this.searchTerm || !newTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newTerm);

    this.searchTerm = newTerm;

    if (!newTerm.length) {
      this.close(true);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    this.getSearchResults(newTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();
    // toggle icons using cached elements
    const searchIcon = this.searchIcon; // magnifying glass
    const clearIcon = this.clearIcon; // X button

    if (searchIcon && clearIcon) {
      clearIcon.style.display = "block";
      searchIcon.style.display = "none";
    }

    if (!searchTerm.length) {
      this.getSearchResults("Best Seller");
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const searchIcon = this.searchIcon; // magnifying glass
      const clearIcon = this.clearIcon; // X button

      if (searchIcon && clearIcon) {
        clearIcon.style.display = "none";
        searchIcon.style.display = "block";
      }

      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selected = this.querySelector('[aria-selected="true"]');
    const allVisible = Array.from(this.querySelectorAll("li, button")).filter(
      (el) => el.offsetParent !== null
    );

    if (moveUp && !selected) return;

    this.statusElement.textContent = "";

    let newIndex;
    const currentIndex = allVisible.indexOf(selected);

    if (!moveUp) {
      newIndex = currentIndex === allVisible.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex <= 0 ? allVisible.length - 1 : currentIndex - 1;
    }

    const next = allVisible[newIndex];
    if (next === selected) return;

    next.setAttribute("aria-selected", "true");
    selected?.setAttribute("aria-selected", "false");
    this.setLiveRegionText(next.textContent);
    this.input.setAttribute("aria-activedescendant", next.id);
  }

  selectOption() {
    const chosen = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );
    chosen?.click();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const btnTextEl = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    if (!btnTextEl) return;

    const text = btnTextEl.innerText;
    if ((text.match(new RegExp(previousTerm, "g")) || []).length > 1) return;

    btnTextEl.innerText = text.replace(previousTerm, newTerm);
  }

  getSearchResults(searchTerm) {
    const key = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key]);
      return;
    }

    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&section_id=predictive-search`;

    fetch(
      window.Shopify.routes.root +
        `search/suggest.json?q=${encodeURIComponent(searchTerm)}`
    )
      .then((response) => response.json())
      .then((suggestions) => {
        console.log(suggestions);
        const productSuggestions = suggestions.resources.results.products;

        if (productSuggestions.length > 0) {
          const firstProductSuggestion = productSuggestions[0];

          console.log(
            `The title of the first product suggestion is: ${firstProductSuggestion.title}`
          );
        }
      });

    fetch(url, { signal: this.abortController.signal })
      .then((resp) => {
        if (!resp.ok) {
          this.close();
          throw new Error(resp.status);
        }
        return resp.text();
      })
      .then((html) => {
        const fragment = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        this.cachedResults[key] = fragment;
        this.renderSearchResults(fragment);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        this.close();
        throw err;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", "");
  }

  setLiveRegionText(text) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = text;
    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1_000);
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute("results", "true");
    this.setLiveRegionResults();
    this.open();
    if (this.input.value.length > 1) {
      this.querySelector(".predictive-search__static").style.display = "none";
      console.log("hidden");
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countText = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    ).textContent;
    this.setLiveRegionText(countText);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.getElementById("shopify-section-header").getBoundingClientRect()
        .bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", "");
    this.input.setAttribute("aria-expanded", "true");
    this.isOpen = true;
    let clearBtnLoad = document.querySelectorAll(".go-back-btn");
    clearBtnLoad.forEach(function (btn) {
      btn.style.display = "flex";
    });
    if (document.querySelector("predictive-search[open]")) {
      this.overlay.style.opacity = "1";
      this.overlay.style.pointerEvents = "auto";
    }
  }

  close(clear = false) {
    if (clear) {
      this.input.value = "";
      this.searchTerm = "";
      this.removeAttribute("results");
    }
    const sel = this.querySelector('[aria-selected="true"]');
    sel?.setAttribute("aria-selected", "false");

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", "false");
    this.predictiveSearchResults.removeAttribute("style");
    this.isOpen = false;
    let clearBtnLoad = document.querySelectorAll(".go-back-btn");
    clearBtnLoad.forEach(function (btn) {
      btn.style.display = "none";
    });
    if (!document.querySelector("predictive-search[open]")) {
      this.overlay.style.opacity = "0";
      this.overlay.style.pointerEvents = "none";
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
